import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor';
import { insertFromMarkdown } from '@affine/core/blocksuite/utils/markdown-utils';
import { DocModeProvider } from '@blocksuite/affine/shared/services';
import { useService } from '@toeverything/infra';
import { useEffect, useRef } from 'react';

import {
  AGENT_STAGES_CANCEL_EVENT,
  type AgentStagesCancelDetail,
} from '../../../blocksuite/ai/blocks/agent-stages-block/agent-stages-block';
import { WorkspaceCitationStore } from '../../citations';
import { DocsService } from '../../doc';
import { TagService } from '../../tag';
import { WorkspaceService } from '../../workspace';
import { OrchestrationService } from '../services';
import { reduce } from '../state/agent-run-reducer';
import {
  type AgentRunState,
  serialiseAgentRunState,
} from '../state/agent-run-state';
import type { Citation } from '../types';
import { applyResearchHighlights } from '../utils/apply-research-highlights';
import {
  applyAnalyzeKindToDoc,
  applySnippetTagToDoc,
  titleFromSynthesis,
} from '../utils/create-research-doc';
import {
  buildCitationNumberMap,
  dedupeCitationsForDisplay,
  linkifyCitations,
} from '../utils/linkify-citations';
import { sectionsToCallouts } from '../utils/sections-to-callouts';
import { stripLlmSourcesSection } from '../utils/strip-llm-sources';

export interface UseOrchestrationDocWriterOptions {
  editor: AffineEditorContainer | null;
  /**
   * Synchronous getter returning the parent block id (usually an
   * `affine:note`) to append agent activity under. A getter is used
   * instead of a prop so the caller can set it imperatively via a ref
   * *before* firing `executeOrchestration`, eliminating the race
   * between SSE events and React re-renders.
   */
  getNoteBlockId: () => string | null;
  scrollViewportRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Subscribes to orchestration events and writes the full agentic run
 * directly into the BlockSuite doc — a single `affine:agent-stages`
 * block for the workflow timeline plus existing paragraph-based
 * streaming for the synthesis.
 *
 * The stages block owns the live visualization of every step, so we
 * no longer churn the doc with per-step heading blocks. One block,
 * one rAF-batched `doc.updateBlock` per event burst, no 40-entry
 * undo-stack pollution.
 *
 * Post-completion the block flips to `collapsed=true` and becomes a
 * one-line "Research log · 12s · 4 agents · 8 sources" summary the
 * user can re-expand later — a durable audit trail of the run.
 */
export function useOrchestrationDocWriter({
  editor,
  getNoteBlockId,
  scrollViewportRef,
}: UseOrchestrationDocWriterOptions): void {
  const service = useService(OrchestrationService);
  const docsService = useService(DocsService);
  const citationStore = useService(WorkspaceCitationStore);
  const workspaceService = useService(WorkspaceService);
  const tagService = useService(TagService);

  // Per-doc memory of the last title we wrote via the AI. If the
  // current doc title diverges, the user manually renamed and we
  // must not clobber that — but multi-turn refresh is fine while the
  // title still matches what we previously set.
  const lastAiSetTitleRef = useRef<Map<string, string>>(new Map());
  const stagesBlockIdRef = useRef<string | null>(null);
  const runStateRef = useRef<AgentRunState | null>(null);
  const stagesFlushRafRef = useRef<number | null>(null);

  const synthesisBlockIdRef = useRef<string | null>(null);
  const synthesisBufferRef = useRef<string>('');
  const synthesisPlaceholderClearedRef = useRef<boolean>(false);
  const textRafIdRef = useRef<number | null>(null);
  const citationsRef = useRef<Citation[]>([]);
  const getNoteBlockIdRef = useRef(getNoteBlockId);
  const docChainRef = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    getNoteBlockIdRef.current = getNoteBlockId;
  }, [getNoteBlockId]);

  useEffect(() => {
    if (!editor?.host?.std?.store) return;

    const doc = editor.host.std.store;
    const host = editor.host;

    const log = (...args: unknown[]) => console.debug('[doc-writer]', ...args);

    const scrollToBottom = () => {
      const vp = scrollViewportRef?.current;
      if (vp) vp.scrollTo({ top: vp.scrollHeight, behavior: 'smooth' });
    };

    const scrollToBlock = (blockId: string | undefined) => {
      if (!blockId) {
        scrollToBottom();
        return;
      }
      const el = document.querySelector(
        `[data-block-id="${CSS.escape(blockId)}"]`
      );
      if (el && 'scrollIntoView' in el) {
        (el as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else {
        scrollToBottom();
      }
    };

    // The doc is marked user-readonly in Phase 1 (research reading), but
    // BlockSuite's Store guards `addBlock`/`updateBlock`/`deleteBlock` on
    // the same `readonly` flag — so programmatic population by the
    // orchestrator would throw. These helpers briefly flip readonly off
    // around a single write and restore it after, so user input stays
    // blocked outside the write windows.
    const runWithWrites = <T>(fn: () => T): T => {
      const wasReadonly = doc.readonly;
      if (wasReadonly) doc.readonly = false;
      try {
        return fn();
      } finally {
        if (wasReadonly) doc.readonly = true;
      }
    };

    const runWithWritesAsync = async <T>(fn: () => Promise<T>): Promise<T> => {
      const wasReadonly = doc.readonly;
      if (wasReadonly) doc.readonly = false;
      try {
        return await fn();
      } finally {
        if (wasReadonly) doc.readonly = true;
      }
    };

    const safeTransact = <T>(fn: () => T): T | null => {
      try {
        let out: T | null = null;
        runWithWrites(() => {
          doc.transact(() => {
            out = fn();
          });
        });
        return out;
      } catch (err) {
        console.warn('[doc-writer] transact failed:', err);
        return null;
      }
    };

    const deleteBlocksById = (ids: string[]) => {
      runWithWrites(() => {
        for (const id of ids) {
          const model = doc.getBlock(id)?.model;
          if (model) {
            try {
              doc.deleteBlock(model);
            } catch {
              /* already gone */
            }
          }
        }
      });
    };

    const insertMarkdown = async (markdown: string): Promise<string[]> => {
      const parentId = getNoteBlockIdRef.current();
      if (!parentId) return [];
      return runWithWritesAsync(async () => {
        try {
          // insertFromMarkdown defaults `index` to 0 (prepend); pass the
          // current child count so each insert appends at the end.
          const parent = doc.getBlock(parentId)?.model;
          const index = parent?.children?.length ?? undefined;
          const normalised = normaliseGfmAlerts(markdown);
          const models = await insertFromMarkdown(
            host,
            normalised,
            doc,
            parentId,
            index
          );
          return models.map(m => m.id);
        } catch (err) {
          console.warn('[doc-writer] insertFromMarkdown failed:', err);
          return [];
        }
      });
    };

    /**
     * Fire-and-forget: append `work` to the doc-mutation promise chain
     * so SSE wire order is preserved in block order. Errors are
     * caught and logged; callers never see a rejection.
     */
    const enqueue = <T>(work: () => Promise<T> | T): void => {
      const prior = docChainRef.current;
      docChainRef.current = prior
        .then(() => work())
        .catch(err => {
          console.warn('[doc-writer] queued work failed:', err);
        });
    };

    /**
     * Like `enqueue` but returns the work result. Use only when the
     * caller needs the output (e.g. the newly-created block id).
     */
    const enqueueAwait = <T>(work: () => Promise<T> | T): Promise<T> => {
      const prior = docChainRef.current;
      const next = prior
        .then(() => work())
        .catch(err => {
          console.warn('[doc-writer] queued work failed:', err);
          return undefined as unknown as T;
        });
      docChainRef.current = next;
      return next;
    };

    /**
     * Flush the current `AgentRunState` to the stages block prop.
     * rAF-batched so a burst of `step-progress` / `agent-event`
     * updates produces a single CRDT write per frame.
     */
    const flushStages = () => {
      stagesFlushRafRef.current = null;
      const state = runStateRef.current;
      const blockId = stagesBlockIdRef.current;
      if (!state || !blockId) return;
      enqueue(() => {
        const model = doc.getBlock(blockId)?.model;
        if (!model) return;
        safeTransact(() => {
          try {
            doc.updateBlock(model, {
              stagesJson: serialiseAgentRunState(state),
              collapsed: state.collapsed,
            });
          } catch {
            /* ignore */
          }
        });
      });
    };

    const scheduleStagesFlush = (immediate = false) => {
      if (immediate) {
        if (stagesFlushRafRef.current !== null) {
          cancelAnimationFrame(stagesFlushRafRef.current);
          stagesFlushRafRef.current = null;
        }
        flushStages();
        return;
      }
      if (stagesFlushRafRef.current !== null) return;
      stagesFlushRafRef.current = requestAnimationFrame(flushStages);
    };

    /**
     * Create the `affine:agent-stages` block as the first child of this
     * run. Returns the new block id.
     */
    const createStagesBlock = async (
      initial: AgentRunState
    ): Promise<string | null> => {
      const parentId = getNoteBlockIdRef.current();
      if (!parentId) return null;
      return (
        (await enqueueAwait(() => {
          let id: string | null = null;
          safeTransact(() => {
            const parent = doc.getBlock(parentId)?.model;
            const index = parent?.children?.length ?? undefined;
            id = doc.addBlock(
              'affine:agent-stages' as any,
              {
                stagesJson: serialiseAgentRunState(initial),
                collapsed: false,
              },
              parentId,
              index
            );
          });
          return id;
        })) ?? null
      );
    };

    const cancelHandler = (e: Event) => {
      const custom = e as CustomEvent<AgentStagesCancelDetail>;
      const planId = custom.detail?.planId ?? runStateRef.current?.planId;
      if (planId) {
        void service.cancelPlan(planId).catch(err => {
          console.warn('[doc-writer] cancelPlan failed:', err);
        });
      }
    };
    document.addEventListener(AGENT_STAGES_CANCEL_EVENT, cancelHandler);

    const subscription = service.events$.subscribe(event => {
      const parentId = getNoteBlockIdRef.current();
      if (!parentId) {
        log('event skipped — no noteBlockId:', event.type);
        return;
      }
      log('event:', event.type);

      // Apply the reducer synchronously for every event so derived
      // state (planId for cancel, etc.) is always current.
      runStateRef.current = reduce(runStateRef.current, event);

      switch (event.type) {
        case 'plan-start': {
          stagesBlockIdRef.current = null;
          synthesisBlockIdRef.current = null;
          synthesisBufferRef.current = '';
          synthesisPlaceholderClearedRef.current = false;
          // Note: we intentionally DO NOT reset `citationsRef` between
          // turns. Citations accumulate across the whole session so the
          // Citations panel and the cross-turn mindmap stay coherent —
          // the user can keep referring back to prior evidence without
          // the list disappearing on each new question.

          // Echo the user's question into the doc as a quote block so
          // the transcript reads as a conversation (Q → A → Q → A …).
          // The text gets a slate background highlight applied via the
          // Yjs `text.format` API — same mechanism as the evidence
          // tags — so every user input shares one consistent palette
          // and never blends into the LLM's prose.
          //
          // When the user attached a selection-excerpt via
          // "Ask Curiescious", prepend that excerpt as a separate
          // quote line so the doc shows the full turn (passage →
          // question → answer) instead of an orphaned "?".
          const userQuery = event.query?.trim();
          const referenceText = event.reference?.text?.trim();
          if (userQuery || referenceText) {
            // Use the markdown adapter (the same path the synthesis
            // uses) to land the quote block reliably — the earlier
            // addBlock+text.insert+text.format chain produced an empty
            // bar because the format() call inside the same CRDT
            // transaction broke the insert commit. After the block
            // materialises we apply the slate highlight in a separate
            // transaction. Newlines collapsed so a multi-line paste
            // stays as ONE quote block instead of fragmenting.
            const quoteLines: string[] = [];
            if (referenceText) {
              quoteLines.push(
                `> 📎 _Selected passage:_ ${referenceText.replace(/\n+/g, ' ')}`
              );
            }
            if (userQuery) {
              quoteLines.push(`> ${userQuery.replace(/\n+/g, ' ')}`);
            }
            const md = quoteLines.join('\n>\n');
            enqueue(async () => {
              try {
                // BlockSuite seeds every new doc with one empty
                // affine:paragraph as the first child of the note.
                // With the doc title hidden, that empty paragraph
                // is the only thing between the chrome and the
                // user-query quote — a fat band of whitespace.
                // Strip it before inserting if it's still empty
                // (i.e. the user hasn't typed into it).
                safeTransact(() => {
                  const parent = doc.getBlock(parentId)?.model;
                  const first = parent?.children?.[0];
                  if (!first) return;
                  const hasEmptyText =
                    'text' in first &&
                    (first as { text?: { toString(): string } }).text
                      ?.toString()
                      .trim() === '';
                  const isPlainParagraph = first.flavour === 'affine:paragraph';
                  if (isPlainParagraph && hasEmptyText) {
                    doc.deleteBlock(first);
                  }
                });
                const ids = await insertMarkdown(md);
                if (ids.length === 0) return;
                safeTransact(() => {
                  const model = doc.getBlock(ids[0])?.model;
                  if (!model || !('text' in model)) return;
                  const text = (
                    model as {
                      text: {
                        toString: () => string;
                        format: (
                          i: number,
                          l: number,
                          a: Record<string, unknown>
                        ) => void;
                      };
                    }
                  ).text;
                  const len = text.toString().length;
                  if (len > 0) {
                    text.format(0, len, {
                      background: '#f1f5f9',
                      color: '#334155',
                    });
                  }
                });
              } catch (err) {
                console.warn('[doc-writer] user-query block failed:', err);
              }
            });
          }

          const initial = runStateRef.current;
          if (initial) {
            createStagesBlock(initial)
              .then(id => {
                if (id) {
                  stagesBlockIdRef.current = id;
                  scrollToBlock(id);
                }
              })
              .catch(err => {
                console.warn('[doc-writer] createStagesBlock failed:', err);
              });
          }
          break;
        }

        case 'plan-ready':
        case 'step-start':
        case 'step-progress':
        case 'step-complete':
        case 'step-error':
        case 'agent-event':
        case 'synthesis-progress':
        case 'missing-citation':
          scheduleStagesFlush();
          break;

        case 'citation': {
          const key = `${event.citation.type ?? ''}:${event.citation.identifier ?? ''}`;
          const existingIdx = citationsRef.current.findIndex(
            c => `${c.type ?? ''}:${c.identifier ?? ''}` === key
          );
          if (existingIdx === -1) {
            citationsRef.current.push(event.citation);
          } else {
            citationsRef.current[existingIdx] = {
              ...citationsRef.current[existingIdx],
              ...event.citation,
            };
          }
          citationStore.upsertMany([event.citation]);
          scheduleStagesFlush();
          break;
        }

        case 'synthesis-start': {
          scheduleStagesFlush();
          synthesisPlaceholderClearedRef.current = false;
          enqueue(async () => {
            safeTransact(() => {
              const blockId = doc.addBlock(
                'affine:paragraph' as any,
                { type: 'text' },
                parentId
              );
              synthesisBlockIdRef.current = blockId;
              const block = doc.getBlock(blockId);
              if (block?.model && 'text' in block.model) {
                try {
                  (block.model as any).text.insert(
                    '⚡ Generating response…',
                    0
                  );
                } catch {
                  /* ignore */
                }
              }
            });
            scrollToBottom();
          });
          break;
        }

        case 'text-delta': {
          synthesisBufferRef.current += event.text;
          if (!synthesisPlaceholderClearedRef.current) {
            synthesisPlaceholderClearedRef.current = true;
            enqueue(() => {
              const id = synthesisBlockIdRef.current;
              if (!id) return;
              const model = doc.getBlock(id)?.model;
              if (!model || !('text' in model)) return;
              safeTransact(() => {
                try {
                  const len = (model as any).text.toString().length;
                  (model as any).text.delete(0, len);
                } catch {
                  /* ignore */
                }
              });
            });
          }

          if (textRafIdRef.current === null) {
            textRafIdRef.current = requestAnimationFrame(() => {
              textRafIdRef.current = null;
              const pending = synthesisBufferRef.current;
              synthesisBufferRef.current = '';
              enqueue(() => {
                const id = synthesisBlockIdRef.current;
                if (!id) return;
                const model = doc.getBlock(id)?.model;
                if (!model || !('text' in model)) return;
                safeTransact(() => {
                  try {
                    const textObj = (model as any).text;
                    const len = textObj.toString().length;
                    textObj.insert(pending, len);
                  } catch {
                    /* ignore */
                  }
                });
                scrollToBottom();
              });
            });
          }
          break;
        }

        case 'complete': {
          if (textRafIdRef.current !== null) {
            cancelAnimationFrame(textRafIdRef.current);
            textRafIdRef.current = null;
          }

          // Phase B: short-circuit path. Backend returned a clarification
          // without invoking any agents. Tear down the stages block we
          // optimistically created on plan-start and write a friendly
          // callout instead — no empty TL;DR / Evidence skeleton.
          const clarification = event.result.clarification;
          if (clarification) {
            if (stagesBlockIdRef.current) {
              deleteBlocksById([stagesBlockIdRef.current]);
              stagesBlockIdRef.current = null;
            }
            const bullets = clarification.suggestions
              .map(s => `- ${s}`)
              .join('\n');
            const md = `> ${clarification.message}\n\n${bullets}`;
            enqueue(() => insertMarkdown(md));
            break;
          }

          // Reducer already flipped collapsed=true; push it immediately
          // so the UI collapse happens in the same frame as the final
          // synthesis landing.
          scheduleStagesFlush(true);

          // Final reconciliation: the backend's canonical citation list
          // on the result carries verification updates that may have
          // arrived after the streamed `citation` events. Upserting
          // closes the gap so the workspace store stays authoritative.
          if (event.result.citations?.length) {
            citationStore.upsertMany(event.result.citations);

            // Phase 7: persist to the durable `workspace_citations`
            // table. Fire-and-forget; failures are logged but don't
            // block the synthesis render. `sessionId` is included
            // when available so the `WorkspaceCitationRef` row can
            // point back at the originating chat turn.
            const workspaceId = workspaceService.workspace.id;
            if (workspaceId) {
              void citationStore.persist({
                workspaceId,
                docId: doc.id,
                sessionId: (event.result as any).sessionId,
                citations: event.result.citations,
              });
            }
          }

          // Analyze-mode branch: when the handler attached a structured
          // `analyzeResult` (Phase A), render it as a method-transparent
          // markdown block — narrative + statistics table + method +
          // caveats + next-step chips. Phase C will replace this with a
          // dedicated `affine:analyze-result` BlockSuite block, but the
          // markdown form is the right Phase-A landing target: it ships
          // a usable doc immediately and shares the Explore rendering
          // path (insertMarkdown / readonly-toggle / streaming flow).
          //
          // When the dataset profile is present but no structured
          // `analyzeResult` (e.g. the handler took the `executeCustom`
          // LLM path because the query didn't match an IC50/stats/
          // correlation pattern), we still want the dataset preview +
          // suggested-analysis chips at the top — but we let the
          // synthesis path below ALSO render the narrative. That keeps
          // non-dose-response runs (DEG / survival / HTS) useful.
          const analyzePayload = (
            event.result as {
              analyzeResult?: unknown;
              datasetProfile?: unknown;
            }
          ).analyzeResult;
          const datasetProfilePayload = (
            event.result as { datasetProfile?: unknown }
          ).datasetProfile;

          // Deep-research-mode branch (Phase A). The handler returns
          // 5 structured fields (bundleSummary, documentAnalyses,
          // crossDocumentPatterns, conflictsAndGaps, synthesisStructure).
          // Render them as markdown sections beneath the synthesis
          // narrative — same pattern as analyze, but no dedicated card
          // block yet (Phase C lands that).
          const drBundleSummary = (event.result as { bundleSummary?: unknown })
            .bundleSummary;
          const drDocumentAnalyses = (
            event.result as { documentAnalyses?: unknown }
          ).documentAnalyses;
          const drPatterns = (
            event.result as { crossDocumentPatterns?: unknown }
          ).crossDocumentPatterns;
          const drConflicts = (event.result as { conflictsAndGaps?: unknown })
            .conflictsAndGaps;
          const drSynthesisStructure = (
            event.result as { synthesisStructure?: unknown }
          ).synthesisStructure;
          const drExtractionRows = (
            event.result as { extractionRows?: unknown }
          ).extractionRows;
          const drStructuredConflicts = (
            event.result as { conflicts?: unknown }
          ).conflicts;
          const drGaps = (event.result as { gaps?: unknown }).gaps;
          const drMethodsParagraph = (
            event.result as { methodsParagraph?: unknown }
          ).methodsParagraph;
          const drMethodsParagraphStyle = (
            event.result as { methodsParagraphStyle?: unknown }
          ).methodsParagraphStyle;
          const drCapsule = (event.result as { capsule?: unknown }).capsule;
          const drRetractionFlags = (
            event.result as { retractionFlags?: unknown }
          ).retractionFlags;
          const drProcessingNotes = (
            event.result as { processingNotes?: unknown }
          ).processingNotes;
          const drSuggestedAdditions = (
            event.result as { suggestedAdditions?: unknown }
          ).suggestedAdditions;
          const hasDeepResearch =
            !!drBundleSummary ||
            !!drDocumentAnalyses ||
            !!drPatterns ||
            !!drConflicts ||
            !!drSynthesisStructure ||
            !!drExtractionRows ||
            !!drStructuredConflicts ||
            !!drGaps ||
            !!drMethodsParagraph ||
            !!drCapsule ||
            !!drRetractionFlags;
          if (hasDeepResearch) {
            const drPayload: DeepResearchPayloadForRender = {
              bundleSummary: drBundleSummary as
                | DeepResearchBundleSummaryForRender
                | undefined,
              documentAnalyses: drDocumentAnalyses as
                | DeepResearchDocAnalysisForRender[]
                | undefined,
              crossDocumentPatterns: drPatterns as
                | DeepResearchPatternForRender[]
                | undefined,
              conflictsAndGaps: drConflicts as
                | DeepResearchConflictForRender[]
                | undefined,
              synthesisStructure: drSynthesisStructure as
                | DeepResearchSynthesisForRender
                | undefined,
              extractionRows: drExtractionRows as
                | DeepResearchExtractionRowForRender[]
                | undefined,
              conflicts: drStructuredConflicts as
                | DeepResearchStrictConflictForRender[]
                | undefined,
              gaps: drGaps as DeepResearchGapForRender[] | undefined,
              methodsParagraph: drMethodsParagraph as string | undefined,
              methodsParagraphStyle: drMethodsParagraphStyle as
                | string
                | undefined,
              capsule: drCapsule as DeepResearchCapsuleForRender | undefined,
              retractionFlags: drRetractionFlags as
                | DeepResearchRetractionFlagForRender[]
                | undefined,
              processingNotes: drProcessingNotes as string[] | undefined,
              suggestedAdditions: drSuggestedAdditions as
                | Array<{
                    theme?: string;
                    title?: string;
                    pmid?: string;
                    year?: number;
                    journal?: string;
                  }>
                | undefined,
            };
            const drMd = renderDeepResearchMarkdown(drPayload);
            // Phase C: drop the interactive deep-research-result card
            // above the markdown so Download bundle / Copy hash /
            // Regenerate buttons sit at the top of the result.
            const insertDeepResearchBlock = () => {
              const noteId = getNoteBlockId();
              if (!noteId) return;
              safeTransact(() => {
                try {
                  doc.addBlock(
                    'affine:deep-research-result',
                    {
                      deepResearchResultJson: JSON.stringify({
                        capsule: drPayload.capsule,
                        methodsParagraphStyle: drPayload.methodsParagraphStyle,
                        extractionRows: drPayload.extractionRows,
                        conflicts: drPayload.conflicts,
                        gaps: drPayload.gaps,
                        // Gap-4 — feed documentAnalyses (with chunks)
                        // into the block so its BundleSearch BM25
                        // index has the full per-doc text to search.
                        documentAnalyses: drPayload.documentAnalyses,
                      }),
                      bundleSummaryJson: drPayload.bundleSummary
                        ? JSON.stringify(drPayload.bundleSummary)
                        : '',
                    },
                    noteId
                  );
                } catch (err) {
                  console.warn(
                    '[doc-writer] failed to insert deep-research-result block:',
                    err
                  );
                }
              });
            };
            enqueue(async () => {
              const pending = synthesisBufferRef.current;
              const placeholderId = synthesisBlockIdRef.current;
              // Replace the streamed synthesis placeholder with the
              // structured DR markdown — the structured form is the
              // canonical answer, just like analyze.
              synthesisBufferRef.current = '';
              synthesisBlockIdRef.current = null;
              if (placeholderId) {
                if (pending) {
                  const model = doc.getBlock(placeholderId)?.model;
                  if (model && 'text' in model) {
                    safeTransact(() => {
                      try {
                        const textObj = (model as any).text;
                        const len = textObj.toString().length;
                        textObj.insert(pending, len);
                      } catch {
                        /* ignore */
                      }
                    });
                  }
                }
                safeTransact(() => deleteBlocksById([placeholderId]));
              }
              // Drop the custom DR card first (Download / Copy /
              // Regenerate buttons), then the markdown body beneath.
              insertDeepResearchBlock();
              if (drMd) await insertMarkdown(drMd);
              scrollToBottom();
            });
            break;
          }
          if (analyzePayload || datasetProfilePayload) {
            const analyzeMd = renderAnalyzeMarkdown(
              datasetProfilePayload as AnalyzeProfileForRender | undefined,
              analyzePayload as AnalyzeResultForRender | undefined
            );
            const hasStructuredResult = !!analyzePayload;
            // Phase E: drop a custom `affine:analyze-result` block at
            // the top of the result whenever we have either a
            // structured analyzeResult (→ Download .ipynb + capsule
            // metadata) or a dataset profile alone (→ at-a-glance
            // metadata + HTS plate grid for plate datasets).
            // Markdown body still renders below for narrative + tables.
            const insertAnalyzeBlock =
              hasStructuredResult || !!datasetProfilePayload
                ? () => {
                    const noteId = getNoteBlockId();
                    if (!noteId) return;
                    safeTransact(() => {
                      try {
                        doc.addBlock(
                          'affine:analyze-result',
                          {
                            analyzeResultJson: analyzePayload
                              ? JSON.stringify(analyzePayload)
                              : '',
                            datasetProfileJson: datasetProfilePayload
                              ? JSON.stringify(datasetProfilePayload)
                              : '',
                          },
                          noteId
                        );
                      } catch (err) {
                        console.warn(
                          '[doc-writer] failed to insert analyze-result block:',
                          err
                        );
                      }
                    });
                  }
                : null;
            // Phase D step 3: tag the doc with the detected dataset
            // kind so it surfaces in the existing sidebar tag-filter
            // UI ("show me all my dose-response analyses"). Idempotent
            // — re-runs in the same doc just no-op the tag attach.
            const kind = (
              datasetProfilePayload as { kind?: string } | undefined
            )?.kind;
            if (kind) {
              applyAnalyzeKindToDoc({
                tagService,
                docId: doc.id,
                kind,
              });
            }
            // Phase E: explicit "save as snippet" tag, when the
            // backend detected the intent in the user's query.
            const markAsSnippet = (
              analyzePayload as { markAsSnippet?: boolean } | undefined
            )?.markAsSnippet;
            if (markAsSnippet) {
              applySnippetTagToDoc({ tagService, docId: doc.id });
            }
            enqueue(async () => {
              const pending = synthesisBufferRef.current;
              const placeholderId = synthesisBlockIdRef.current;
              if (hasStructuredResult) {
                // Replace the streamed synthesis placeholder with the
                // structured analyze markdown — the structured form is
                // the canonical answer.
                synthesisBufferRef.current = '';
                synthesisBlockIdRef.current = null;
                if (placeholderId) {
                  if (pending) {
                    const model = doc.getBlock(placeholderId)?.model;
                    if (model && 'text' in model) {
                      safeTransact(() => {
                        try {
                          const textObj = (model as any).text;
                          const len = textObj.toString().length;
                          textObj.insert(pending, len);
                        } catch {
                          /* ignore */
                        }
                      });
                    }
                  }
                  safeTransact(() => deleteBlocksById([placeholderId]));
                }
              }
              // Phase E: drop the interactive analyze-result card
              // first, then the markdown body beneath it. Order
              // matters: the card lives at the top of the result for
              // immediate access to Download .ipynb / Copy hash.
              if (insertAnalyzeBlock) insertAnalyzeBlock();
              if (analyzeMd) await insertMarkdown(analyzeMd);
              scrollToBottom();
            });
            if (hasStructuredResult) {
              break;
            }
            // Fall through to the synthesis branch so the LLM-generated
            // narrative still lands beneath the profile preview.
          }

          // Compose synthesis: strip the LLM's bare Sources section (we
          // append a canonical URL-linked one below), then linkify
          // inline citation tokens. Visual emphasis (evidence-grade
          // colour tags, status badges) is applied post-insertion via
          // `applyResearchHighlights` — Yjs Text supports inline
          // background/colour even though markdown can't round-trip
          // them.
          const markdown = linkifyCitations(
            stripLlmSourcesSection(event.result.synthesis ?? ''),
            {
              citations: citationsRef.current,
              useJournalLabel: true,
              // Render inline tokens as numbered `[n]` markers matching the
              // 📚 Sources list below (same dedup ordering).
              numberByKey: buildCitationNumberMap(citationsRef.current),
            }
          );

          // Rendering strategy for Explore-mode synthesis:
          //   - Insert the markdown into the main note so page mode keeps
          //     a readable research document.
          //
          // Auto-building a mind map from the agent's response has been
          // removed — researchers now curate the mindmap manually via
          // the "Add to Mindmap" toolbar button on the selection menu.
          const modeService = host.std.getOptional(DocModeProvider);
          const editorMode = modeService?.getEditorMode() ?? 'unknown';
          console.debug(
            '[doc-writer] complete event:',
            'mode=',
            editorMode,
            'mdLen=',
            markdown.length
          );

          enqueue(async () => {
            const pending = synthesisBufferRef.current;
            synthesisBufferRef.current = '';
            const placeholderId = synthesisBlockIdRef.current;
            synthesisBlockIdRef.current = null;
            if (placeholderId) {
              if (pending) {
                const model = doc.getBlock(placeholderId)?.model;
                if (model && 'text' in model) {
                  safeTransact(() => {
                    try {
                      const textObj = (model as any).text;
                      const len = textObj.toString().length;
                      textObj.insert(pending, len);
                    } catch {
                      /* ignore */
                    }
                  });
                }
              }
              safeTransact(() => deleteBlocksById([placeholderId]));
            }

            const insertedIds = await insertMarkdown(markdown);

            // Append the backend's structured `suggestedFollowUps` as
            // a Next-Steps section IF the LLM didn't already write one
            // in the synthesis. For short / generic queries the model
            // skips Next Steps and these would otherwise be lost.
            const followUps = (event.result.suggestedFollowUps ?? [])
              .map(s => s?.trim())
              .filter((s): s is string => !!s && s.length > 0)
              .slice(0, 6);
            let followUpIds: string[] = [];
            if (followUps.length > 0) {
              const hasNextStepsHeading = insertedIds.some(id => {
                const m = doc.getBlock(id)?.model as
                  | {
                      flavour?: string;
                      type?: string;
                      text?: { toString: () => string };
                    }
                  | undefined;
                if (!m || m.flavour !== 'affine:paragraph') return false;
                if (m.type !== 'h2' && m.type !== 'h3' && m.type !== 'h4') {
                  return false;
                }
                const t = (m.text?.toString() ?? '').trim();
                return /^(next\s+steps|next\s+directions|suggested\s+follow.?ups?)\b/i.test(
                  t
                );
              });
              if (!hasNextStepsHeading) {
                const bullets = followUps.map(s => `- ${s}`).join('\n');
                const md = `### Suggested follow-ups\n\n${bullets}`;
                try {
                  followUpIds = await insertMarkdown(md);
                } catch (err) {
                  console.warn('[doc-writer] follow-ups insert failed:', err);
                }
              }
            }

            // Replace TL;DR / Conflicts / Open Questions / Next Steps
            // sections with affine:callout blocks for scannable
            // visual hierarchy. Sources heading is intentionally
            // skipped — researchers expect a real bibliography there.
            try {
              sectionsToCallouts(
                doc,
                [...insertedIds, ...followUpIds],
                runWithWrites
              );
            } catch (err) {
              console.warn('[doc-writer] sectionsToCallouts failed:', err);
            }
            // Paint inline highlight colours onto evidence-grade
            // markers and status badges. Walks from the parent note so
            // it covers every descendant including new callout
            // children. Idempotent — safe to re-run on prior turns.
            const noteParentId = getNoteBlockIdRef.current();
            if (noteParentId) {
              try {
                applyResearchHighlights(doc, [noteParentId], runWithWrites);
              } catch (err) {
                console.warn(
                  '[doc-writer] applyResearchHighlights failed:',
                  err
                );
              }
            }
            scrollToBottom();

            const sourcesMd = formatSourcesBlock(citationsRef.current);
            if (sourcesMd) {
              await insertMarkdown(sourcesMd);
              scrollToBottom();
            }

            // === Refine the doc title + annotate metadata ===
            // Thread-context title: derive from the AI's actual findings
            // (keyFindings[0] → first sentence of synthesis) so the
            // sidebar reflects what the agent concluded, not what the
            // user pasted in. Falls back to the markdown-heading
            // extractor when the structured signals are empty.
            //
            // Re-titles on every completion so multi-turn conversations
            // track the latest topic — but only while the current title
            // is still one we set ourselves (i.e. the user hasn't
            // manually renamed the doc).
            const docIdForTitle = doc.id;
            const refinedTitle =
              titleFromSynthesis({
                keyFindings: event.result.keyFindings,
                synthesis: event.result.synthesis,
              }) ?? extractTitleFromMarkdown(markdown);
            if (refinedTitle && docIdForTitle) {
              try {
                const record = docsService.list.doc$(docIdForTitle).value;
                const currentTitle = record?.meta$.value?.title?.trim() ?? '';
                const queryText =
                  record?.properties$.value?.['custom:queryText'];
                const lastAiSetTitle =
                  lastAiSetTitleRef.current.get(docIdForTitle);
                // We may safely overwrite if the current title is:
                //   • empty / Untitled
                //   • the query-derived placeholder we set on submit
                //   • the last AI-set title we wrote (multi-turn refresh)
                // Anything else means the user renamed → leave alone.
                const safeToOverwrite =
                  currentTitle === '' ||
                  currentTitle === 'Untitled' ||
                  currentTitle === queryText ||
                  currentTitle === lastAiSetTitle;
                // Don't downgrade: skip if the new title is short and
                // already a strict substring of the existing one.
                const isDowngrade =
                  refinedTitle.length < 12 &&
                  currentTitle.length > refinedTitle.length &&
                  currentTitle
                    .toLowerCase()
                    .includes(refinedTitle.toLowerCase());
                if (
                  safeToOverwrite &&
                  !isDowngrade &&
                  currentTitle !== refinedTitle
                ) {
                  await docsService.changeDocTitle(docIdForTitle, refinedTitle);
                  lastAiSetTitleRef.current.set(docIdForTitle, refinedTitle);
                }
              } catch (err) {
                console.warn('[doc-writer] failed to refine title:', err);
              }
            }

            // Annotate completion metadata (citations count, agents, time).
            if (docIdForTitle) {
              try {
                const record = docsService.list.doc$(docIdForTitle).value;
                if (record) {
                  record.setCustomProperty(
                    'citationCount',
                    String(citationsRef.current.length)
                  );
                  record.setCustomProperty(
                    'completedAt',
                    new Date().toISOString()
                  );
                  const agents = runStateRef.current?.agentsSeen ?? [];
                  if (agents.length > 0) {
                    record.setCustomProperty('agentsUsed', agents.join(','));
                  }
                }
              } catch (err) {
                console.warn(
                  '[doc-writer] failed to annotate completion metadata:',
                  err
                );
              }
            }
          });
          break;
        }

        case 'error': {
          scheduleStagesFlush(true);
          if (textRafIdRef.current !== null) {
            cancelAnimationFrame(textRafIdRef.current);
            textRafIdRef.current = null;
          }
          enqueue(async () => {
            const placeholderId = synthesisBlockIdRef.current;
            synthesisBlockIdRef.current = null;
            if (placeholderId) {
              safeTransact(() => deleteBlocksById([placeholderId]));
            }
          });
          break;
        }

        default:
          break;
      }
    });

    return () => {
      subscription.unsubscribe();
      document.removeEventListener(AGENT_STAGES_CANCEL_EVENT, cancelHandler);
      if (textRafIdRef.current !== null) {
        cancelAnimationFrame(textRafIdRef.current);
        textRafIdRef.current = null;
      }
      if (stagesFlushRafRef.current !== null) {
        cancelAnimationFrame(stagesFlushRafRef.current);
        stagesFlushRafRef.current = null;
      }
    };
  }, [
    editor,
    service,
    scrollViewportRef,
    docsService,
    citationStore,
    workspaceService,
    tagService,
  ]);
}

/**
 * Derive a good sidebar title from the synthesis markdown. Tries in order:
 *   1. First top-level heading (`# ...`)
 *   2. First H2 (`## ...`) — most Explore responses skip H1 and start here
 *   3. First non-empty paragraph's first sentence, capped at 60 chars
 *
 * Returns `null` when nothing usable is found (callers keep the existing
 * title).
 */
function extractTitleFromMarkdown(markdown: string): string | null {
  if (!markdown) return null;
  const lines = markdown.split('\n');

  // Prefer H1, then H2.
  for (const depth of [1, 2]) {
    const marker = '#'.repeat(depth) + ' ';
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith(marker) && !trimmed.startsWith(marker + '#')) {
        const candidate = trimmed.slice(marker.length).trim();
        const cleaned = stripMarkdownInline(candidate);
        if (cleaned.length >= 6) return truncateTitle(cleaned);
      }
    }
  }

  // Fallback: first sentence of first meaningful paragraph.
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith('#') || trimmed.startsWith('>')) continue;
    const cleaned = stripMarkdownInline(trimmed);
    const firstSentence = cleaned.split(/[.!?]/)[0]?.trim() ?? '';
    if (firstSentence.length >= 12) return truncateTitle(firstSentence);
  }

  return null;
}

function stripMarkdownInline(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [text](url) → text
    .replace(/[*_`]+/g, '') // strip emphasis/code markers
    .replace(/\s+/g, ' ')
    .trim();
}

function truncateTitle(text: string): string {
  if (text.length <= 60) return text;
  return text.slice(0, 57).trimEnd() + '…';
}

// ======== Formatting helpers ========

/**
 * Build the trailing `### 📚 Sources` block rendered at the end of a
 * deep-research turn. Every citation is rendered as a two-line entry:
 *
 *   1. **[Title](url)**
 *      Author et al. · 2024 · Journal · ✓ Verified
 *
 * Badges:
 *   ✓ Verified — citation came back verified from PubMed / Crossref / arXiv
 *   ⚠️ Preprint — arXiv / bioRxiv (treat with care)
 *   ❌ Retracted — flagged retracted by the publisher
 *
 * The two-space trailing hard-break + 3-space continuation indent keeps
 * the secondary line within the same list-item per CommonMark, so the
 * markdown adapter emits paired paragraph models with consistent
 * visual rhythm.
 */
function formatSourcesBlock(citations: Citation[]): string | null {
  if (!citations || citations.length === 0) return null;

  // Deduplicate by canonical key (collapsing arXiv versions) so a paper
  // cited several times in the synthesis appears once. Shared with
  // `buildCitationNumberMap` so the numbered inline `[n]` markers and
  // these Sources entries always carry the same numbers.
  const unique = dedupeCitationsForDisplay(citations);

  const lines: string[] = ['### 📚 Sources', ''];
  let idx = 0;
  for (const c of unique) {
    if (!c.identifier) continue;
    idx += 1;
    const url = citationUrl(c);
    const title = sourceTitleLine(c);
    const safeTitle = escapeMd(title).slice(0, 200);
    const meta = sourceMetaLine(c);
    const titleLine = url
      ? `${idx}. **[${safeTitle}](${url})**`
      : `${idx}. **${safeTitle}**`;
    // Trailing two spaces = hard-break; 3 spaces of indent on the next
    // line keep the meta inside the same list-item.
    lines.push(`${titleLine}  `);
    if (meta) lines.push(`   ${meta}`);
    lines.push('');
    if (idx >= 50) break;
  }
  return idx > 0 ? lines.join('\n').trimEnd() : null;
}

/**
 * Normalised citation kind. The backend emits `pmid` while some legacy
 * paths use `pubmed`; both map to `pmid` here. Falls back to `sourceType`
 * when `type` is absent.
 */
function citationKind(c: Citation): string {
  const t = (
    c.type ??
    (c as { sourceType?: string }).sourceType ??
    ''
  ).toLowerCase();
  return t === 'pubmed' ? 'pmid' : t;
}

function sourceTitleLine(c: Citation): string {
  const title = c.title?.trim();
  if (title) return title;
  if (c.identifier) {
    const kind = citationKind(c);
    const label =
      kind === 'pmid'
        ? 'PubMed'
        : kind === 'doi'
          ? 'DOI'
          : kind === 'arxiv'
            ? 'arXiv'
            : '';
    return label ? `${label} ${c.identifier}` : c.identifier;
  }
  return 'Untitled source';
}

function sourceMetaLine(c: Citation): string {
  const parts: string[] = [];
  if (c.authors?.length) {
    parts.push(
      c.authors.length === 1 ? c.authors[0] : `${c.authors[0]} et al.`
    );
  }
  if (c.year) parts.push(String(c.year));
  if (c.journal) parts.push(c.journal);
  // Honest verification state, mirroring the citation-card badges so this
  // inline Sources list is the single source of truth: Retracted ▸
  // Preprint ▸ Verified / Unverified, plus a low-confidence flag.
  const badges: string[] = [];
  if ((c as { retracted?: boolean }).retracted === true) {
    badges.push('Retracted');
  } else if (citationKind(c) === 'arxiv') {
    badges.push('Preprint');
  } else if (c.verified === true) {
    badges.push('Verified');
  } else if (c.verified === false) {
    badges.push('Unverified');
  }
  const confidence = (c as { confidence?: number }).confidence;
  if (typeof confidence === 'number' && confidence < 0.4) {
    badges.push('Low confidence');
  }
  // Badge words stay plain text here; `applyResearchHighlights` paints
  // them with mature low-saturation colour tints post-insertion.
  const metaParts = [...parts.map(escapeMd), ...badges];
  return metaParts.join(' · ');
}

function citationUrl(c: Citation): string | null {
  if (c.url) return c.url;
  if (!c.identifier) return null;
  switch (citationKind(c)) {
    case 'pmid':
      return `https://pubmed.ncbi.nlm.nih.gov/${c.identifier}/`;
    case 'doi':
      return `https://doi.org/${c.identifier}`;
    case 'arxiv':
      return `https://arxiv.org/abs/${c.identifier}`;
    default:
      return null;
  }
}

function escapeMd(s: string): string {
  return s.replace(/([*_`[\]])/g, '\\$1');
}

// ======== GFM alert normalisation ========

const ALERT_EMOJI: Record<string, string> = {
  NOTE: 'ℹ️',
  TIP: '💡',
  IMPORTANT: '❗',
  WARNING: '⚠️',
  CAUTION: '⛔',
};

const ALERT_LABEL: Record<string, string> = {
  NOTE: 'Note',
  TIP: 'Tip',
  IMPORTANT: 'Important',
  WARNING: 'Warning',
  CAUTION: 'Caution',
};

/**
 * Rewrite GFM-alert blockquotes so they survive BlockSuite's markdown
 * adapter as a single emoji-prefixed quote paragraph. The adapter has
 * no handler for `> [!NOTE]` alerts — a naïve pass-through produces
 * fragmented `[!NOTE]` + body paragraphs that lose the callout
 * affordance.
 */
export function normaliseGfmAlerts(markdown: string): string {
  const lines = markdown.split('\n');
  const out: string[] = [];
  let i = 0;
  const alertHeader =
    /^\s*>\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*(.*)$/;

  while (i < lines.length) {
    const m = lines[i].match(alertHeader);
    if (!m) {
      out.push(lines[i]);
      i++;
      continue;
    }
    const kind = m[1] as keyof typeof ALERT_EMOJI;
    const emoji = ALERT_EMOJI[kind];
    const label = ALERT_LABEL[kind];
    const headerRemainder = m[2]?.trim() ?? '';

    const bodyLines: string[] = [];
    let j = i + 1;
    while (j < lines.length) {
      const raw = lines[j];
      if (/^\s*>\s?/.test(raw)) {
        bodyLines.push(raw.replace(/^\s*>\s?/, ''));
        j++;
      } else {
        break;
      }
    }

    const proseParts: string[] = [];
    const trailingListLines: string[] = [];
    let inList = false;
    for (const line of bodyLines) {
      if (/^\s*[-*+]\s+|^\s*\d+\.\s+/.test(line)) {
        inList = true;
      }
      if (inList) {
        trailingListLines.push(line);
      } else if (line.trim().length > 0) {
        proseParts.push(line.trim());
      }
    }

    const prose = [headerRemainder, ...proseParts]
      .filter(s => s.length > 0)
      .join(' ');
    const headerLine = prose
      ? `> ${emoji} **${label}:** ${prose}`
      : `> ${emoji} **${label}**`;
    out.push(headerLine);

    if (trailingListLines.length > 0) {
      out.push('');
      for (const line of trailingListLines) {
        out.push(line);
      }
    }

    i = j;
  }

  return out.join('\n');
}

// ======== Analyze-mode markdown renderer (Phase A) ========

/**
 * Loose shape for `event.result.analyzeResult`. Mirrors the backend
 * `AnalyzeResult` Zod schema (in `types/analyze.ts`) but kept inline
 * here so the doc-writer doesn't import a backend file. Phase C will
 * replace this whole renderer with a dedicated BlockSuite block;
 * Phase A renders to markdown so we can ship without a custom block.
 */
interface AnalyzeStatForRender {
  name: string;
  value: number;
  unit?: string;
  ci95?: [number, number];
  pValue?: number;
  n?: number;
}
interface AnalyzePlotForRender {
  kind: string;
  title?: string;
  xLabel?: string;
  yLabel?: string;
  data?: Record<string, unknown>;
}
interface AnalyzeResultForRender {
  approach?: string;
  code?: string;
  methodCitation?: { title: string; doi?: string; pmid?: string; url?: string };
  statistics?: AnalyzeStatForRender[];
  plots?: AnalyzePlotForRender[];
  caveats?: string[];
  suggestedNextSteps?: string[];
  // Phase D: paper-ready Methods-section paragraph generated by the
  // backend after the fit. Rendered as a quoted callout so the user
  // can copy it straight into a manuscript draft.
  methodsParagraph?: string;
  methodsParagraphStyle?: string;
  // Phase E: capsule artifact (notebook + seed + pkg lock + file
  // hash). Rendered as a "Reproducibility capsule" section with the
  // full .ipynb inlined as a copyable JSON code block.
  capsule?: AnalyzeCapsuleForRender;
}

interface AnalyzeCapsuleCellForRender {
  cellType: 'code' | 'markdown';
  source: string;
  metadata?: Record<string, unknown>;
}
interface AnalyzeCapsuleForRender {
  cells: AnalyzeCapsuleCellForRender[];
  seed: number | null;
  packageLock: Record<string, string | undefined>;
  inputFile: {
    name: string;
    sha256: string;
    rowCount: number;
    columnCount: number;
  };
  createdAt: string;
  generator: string;
}
interface AnalyzeProfileForRender {
  name?: string;
  rowCount?: number;
  columnCount?: number;
  preview?: Array<Record<string, unknown>>;
  columns?: Array<{ name: string; dtype: string; nullCount: number }>;
  // Phase B: domain-kind detector output. Confidence threshold for
  // surfacing it as a "looks like X" callout is 0.5; below that we
  // render the chip row but tag it as "Maybe — confirm before running."
  kind?: string;
  kindConfidence?: number;
  kindSignals?: string[];
  suggestedAnalyses?: string[];
  // Phase E: ordered list of pipeline steps the agent will run for
  // the detected kind. Renders as a "Proposed pipeline" section
  // below the chip row — the visible governance idiom.
  proposedPipeline?: string[];
}

function renderAnalyzeMarkdown(
  profile: AnalyzeProfileForRender | undefined,
  result: AnalyzeResultForRender | undefined
): string {
  const out: string[] = [];

  if (profile) {
    out.push(`## Dataset preview — ${profile.name ?? 'uploaded data'}`);
    out.push('');
    out.push(
      `${profile.rowCount ?? 0} rows × ${profile.columnCount ?? 0} columns.`
    );
    out.push('');

    // Phase B: domain-kind callout + suggested-analysis chip row.
    // Render only when the preflight produced a non-`unknown` verdict.
    const kind = profile.kind;
    const conf = profile.kindConfidence ?? 0;
    if (kind && kind !== 'unknown') {
      const label = formatDatasetKindLabel(kind);
      const isLowConfidence = conf < 0.6;
      const tag = isLowConfidence
        ? `Maybe a **${label}** dataset (confidence ${(conf * 100).toFixed(0)}% — confirm before running).`
        : `Looks like a **${label}** dataset (confidence ${(conf * 100).toFixed(0)}%).`;
      out.push(`> 🧪 ${tag}`);
      if (profile.kindSignals && profile.kindSignals.length > 0) {
        for (const s of profile.kindSignals) {
          out.push(`>   - ${s}`);
        }
      }
      out.push('');

      if (profile.suggestedAnalyses && profile.suggestedAnalyses.length > 0) {
        out.push('**Suggested analyses:**');
        out.push('');
        for (const s of profile.suggestedAnalyses) {
          // Plain text. Suggested-analyses entries are short labels
          // ("Fit 4PL IC50") but if a longer one slips in, the
          // inline-code styling looks heavy. Bold the label with `*` is
          // also overkill — bullets are enough.
          out.push(`- ${s}`);
        }
        out.push('');
      }

      // Phase E: Hex-style "agent proposes, user accepts" pipeline.
      // The agent already ran (we have the result), but surfacing the
      // pipeline gives the user a transparent recipe + a checklist
      // they can map back to the statistics + plots that follow.
      if (profile.proposedPipeline && profile.proposedPipeline.length > 0) {
        out.push('**Pipeline run:**');
        out.push('');
        for (let i = 0; i < profile.proposedPipeline.length; i++) {
          out.push(`${i + 1}. ${profile.proposedPipeline[i]}`);
        }
        out.push('');
      }
    }

    if (profile.columns && profile.columns.length > 0) {
      out.push('| Column | Type | Nulls |');
      out.push('|---|---|---|');
      for (const c of profile.columns) {
        out.push(`| ${c.name} | ${c.dtype} | ${c.nullCount} |`);
      }
      out.push('');
    }
    if (profile.preview && profile.preview.length > 0) {
      const cols = Object.keys(profile.preview[0] ?? {});
      out.push('**First rows:**');
      out.push('');
      out.push(`| ${cols.join(' | ')} |`);
      out.push(`| ${cols.map(() => '---').join(' | ')} |`);
      for (const row of profile.preview.slice(0, 10)) {
        out.push(`| ${cols.map(c => formatCellForMd(row[c])).join(' | ')} |`);
      }
      out.push('');
    }
  }

  if (result) {
    if (result.approach) {
      out.push('## Result');
      out.push('');
      out.push(result.approach);
      out.push('');
    }

    // Statistics now render in the structured card (the `affine:analyze-result`
    // block's renderStatistics — value / CI / p / q / n with significance
    // styling + click-to-copy). They are no longer emitted as a markdown table
    // here, to avoid a duplicate table beneath the card. The card receives the
    // same `statistics` array via `analyzeResultJson`.

    if (result.plots && result.plots.length > 0) {
      out.push('### Plots');
      out.push('');
      for (const p of result.plots) {
        const svg = renderPlotSvg(p);
        if (svg) {
          // Encode the SVG as a data URL image so BlockSuite's markdown
          // parser turns it into an `affine:image` block. Saves us
          // building a custom block in Phase C while still delivering
          // an inline chart the user can see.
          const dataUrl = svgToDataUrl(svg);
          out.push(`![${p.title ?? p.kind} chart](${dataUrl})`);
          out.push('');
          out.push(
            `*${p.title ?? p.kind}* — ${p.xLabel ?? 'x'} vs ${p.yLabel ?? 'y'}.`
          );
          out.push('');
        } else {
          out.push(
            `- **${p.title ?? p.kind}** — ${p.xLabel ?? 'x'} vs ${p.yLabel ?? 'y'} (kind: \`${p.kind}\`).`
          );
        }
      }
      out.push('');
    }

    if (result.code) {
      out.push('### Method (Python equivalent)');
      out.push('');
      out.push('```python');
      out.push(result.code);
      out.push('```');
      out.push('');
      if (result.methodCitation) {
        const c = result.methodCitation;
        const link = c.doi
          ? `https://doi.org/${c.doi}`
          : c.pmid
            ? `https://pubmed.ncbi.nlm.nih.gov/${c.pmid}/`
            : c.url;
        out.push(`_Citation: ${c.title}${link ? ` ([link](${link}))` : ''}._`);
        out.push('');
      }
    }

    if (result.caveats && result.caveats.length > 0) {
      out.push('### Caveats');
      out.push('');
      for (const c of result.caveats) {
        out.push(`- ${c}`);
      }
      out.push('');
    }

    if (result.suggestedNextSteps && result.suggestedNextSteps.length > 0) {
      out.push('### Suggested next steps');
      out.push('');
      for (const s of result.suggestedNextSteps) {
        out.push(`- ${s}`);
      }
      out.push('');
    }

    // Phase D: paper-ready Methods paragraph. Render as a blockquote
    // so it visually reads as a quotable, ready-to-copy block.
    if (result.methodsParagraph && result.methodsParagraph.trim().length > 0) {
      const styleLabel = result.methodsParagraphStyle
        ? ` (${result.methodsParagraphStyle.charAt(0).toUpperCase()}${result.methodsParagraphStyle.slice(1)} style)`
        : '';
      out.push(`### Methods paragraph${styleLabel}`);
      out.push('');
      out.push('*Paste this into your manuscript\u2019s Methods section.*');
      out.push('');
      // BlockSuite turns each `> ` line into part of one quote block;
      // split on newline so multi-sentence paragraphs render as a
      // single visually-grouped quote rather than collapsing into one
      // long line.
      const lines = result.methodsParagraph.split(/\n+/);
      for (const line of lines) {
        out.push(`> ${line.trim()}`);
      }
      out.push('');
    }

    // Phase E: reproducibility capsule. Renders metadata + the full
    // .ipynb JSON as a copyable code block. The user pastes it into
    // a `.ipynb` file and runs `jupyter nbconvert --execute`. Real
    // one-click download lands with the custom block (task #71).
    if (result.capsule && result.capsule.cells.length > 0) {
      const cap = result.capsule;
      out.push('### Reproducibility capsule');
      out.push('');
      out.push(
        '*Save the JSON below as `analysis.ipynb` and run with `jupyter nbconvert --execute analysis.ipynb` to verify the result.*'
      );
      out.push('');
      out.push(
        `- **Input:** \`${cap.inputFile.name}\` — ${cap.inputFile.rowCount} rows × ${cap.inputFile.columnCount} columns`
      );
      out.push(`- **SHA-256:** \`${cap.inputFile.sha256}\``);
      out.push(`- **Seed:** ${cap.seed ?? 'n/a (deterministic)'}`);
      const pkgs = Object.entries(cap.packageLock)
        .filter(([, v]) => !!v)
        .map(([k, v]) => `${k}=${v}`)
        .join(', ');
      if (pkgs) out.push(`- **Packages:** ${pkgs}`);
      out.push(`- **Generated:** ${cap.createdAt} (${cap.generator})`);
      out.push('');
      out.push('```json');
      out.push(JSON.stringify(capsuleToIpynb(cap), null, 2));
      out.push('```');
      out.push('');
    }
  }

  return out.join('\n');
}

/**
 * Convert a capsule artifact into a valid Jupyter .ipynb v4 JSON
 * structure. Pure data — caller can JSON.stringify and save as a
 * file, or hand off to a download utility once the custom block lands.
 */
function capsuleToIpynb(cap: AnalyzeCapsuleForRender): unknown {
  const cells = cap.cells.map(c => ({
    cell_type: c.cellType,
    metadata: c.metadata ?? {},
    source: c.source
      .split('\n')
      .map((line, i, arr) => (i === arr.length - 1 ? line : line + '\n')),
    ...(c.cellType === 'code' ? { execution_count: null, outputs: [] } : {}),
  }));
  const pythonVersion = cap.packageLock.python ?? '3.11';
  return {
    cells,
    metadata: {
      kernelspec: {
        display_name: `Python ${pythonVersion}`,
        language: 'python',
        name: `python${pythonVersion.split('.')[0]}`,
      },
      language_info: {
        name: 'python',
        version: pythonVersion,
      },
      curiescious: {
        seed: cap.seed,
        packageLock: cap.packageLock,
        inputFile: cap.inputFile,
        createdAt: cap.createdAt,
        generator: cap.generator,
      },
    },
    nbformat: 4,
    nbformat_minor: 5,
  };
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return 'n/a';
  const abs = Math.abs(n);
  if (abs === 0) return '0';
  if (abs >= 1000 || abs < 0.001) return n.toExponential(2);
  return n.toPrecision(4).replace(/\.?0+$/, '');
}

function formatCellForMd(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return formatNumber(v);
  const s = String(v);
  return s.length > 30 ? s.slice(0, 27) + '…' : s;
}

// ======== Phase C: inline SVG chart renderer ========
//
// Renders an `AnalyzePlotForRender` to a self-contained SVG string.
// We then base64-encode it as `data:image/svg+xml;base64,…` and embed
// it in the doc markdown. BlockSuite's markdown importer turns that
// into an `affine:image` block, so the chart shows up inside the doc
// without us building a custom BlockSuite block first. Phase C-full
// (export-SVG button, click-to-exclude, etc.) lands when the custom
// block does.

const SVG_W = 560;
const SVG_H = 320;
const PADDING = { top: 24, right: 24, bottom: 48, left: 56 };
const PLOT_W = SVG_W - PADDING.left - PADDING.right;
const PLOT_H = SVG_H - PADDING.top - PADDING.bottom;

function renderPlotSvg(plot: AnalyzePlotForRender): string | null {
  const x = (plot.data?.x as number[] | undefined) ?? [];
  const y = (plot.data?.y as number[] | undefined) ?? [];
  if (x.length === 0 || y.length === 0 || x.length !== y.length) return null;

  // Dose-response is the only kind we render today; defer the rest
  // (heatmaps, KM curves) to the custom block. For 'scatter' / 'line'
  // we use a generic linear axis; for dose-response (positive xs that
  // span > 1 order of magnitude) we use log10 on the x-axis since
  // that's what biologists expect.
  const useLogX =
    plot.kind === 'scatter' || plot.kind === 'line'
      ? x.every(v => v > 0) && Math.max(...x) / Math.min(...x) >= 100
      : false;

  const xVals = useLogX ? x.map(v => Math.log10(v)) : x.slice();
  const xMin = Math.min(...xVals);
  const xMax = Math.max(...xVals);
  const yMin = Math.min(...y, 0);
  const yMax = Math.max(...y, 0);
  const xRange = xMax - xMin || 1;
  const yRange = yMax - yMin || 1;

  const px = (xv: number): number =>
    PADDING.left + ((xv - xMin) / xRange) * PLOT_W;
  const py = (yv: number): number =>
    PADDING.top + PLOT_H - ((yv - yMin) / yRange) * PLOT_H;

  const points = xVals
    .map(
      (xv, i) =>
        `<circle cx="${px(xv).toFixed(1)}" cy="${py(y[i]).toFixed(1)}" r="3.5" fill="#2563eb" stroke="#1e40af" stroke-width="0.5"/>`
    )
    .join('');

  // Optional fitted curve: backend stashes `curveX` (raw doses) +
  // `yFitted` arrays under `data` so we draw the smooth fit through
  // the scatter.
  const curveX = (plot.data?.curveX as number[] | undefined) ?? [];
  const yFitted = (plot.data?.yFitted as number[] | undefined) ?? [];
  let pathD = '';
  if (curveX.length > 1 && curveX.length === yFitted.length) {
    const cx = useLogX
      ? curveX.map(v => (v > 0 ? Math.log10(v) : xMin))
      : curveX.slice();
    pathD = cx
      .map(
        (xv, i) =>
          `${i === 0 ? 'M' : 'L'}${px(xv).toFixed(1)} ${py(yFitted[i]).toFixed(1)}`
      )
      .join(' ');
  }

  // X-axis tick labels — log scale ⇒ powers of 10; linear ⇒ 5 evenly-
  // spaced ticks.
  const xTicks: number[] = [];
  if (useLogX) {
    const lo = Math.floor(xMin);
    const hi = Math.ceil(xMax);
    for (let v = lo; v <= hi; v++) xTicks.push(v);
  } else {
    const steps = 4;
    for (let i = 0; i <= steps; i++) xTicks.push(xMin + (i / steps) * xRange);
  }
  const xTickLabels = xTicks
    .map(v => {
      const cx = px(v);
      const labelValue = useLogX ? Math.pow(10, v) : v;
      const text = formatTickLabel(labelValue);
      return `<line x1="${cx.toFixed(1)}" y1="${(PADDING.top + PLOT_H).toFixed(1)}" x2="${cx.toFixed(1)}" y2="${(PADDING.top + PLOT_H + 4).toFixed(1)}" stroke="#94a3b8"/><text x="${cx.toFixed(1)}" y="${(PADDING.top + PLOT_H + 18).toFixed(1)}" text-anchor="middle" font-size="10" fill="#475569" font-family="ui-sans-serif, system-ui">${text}</text>`;
    })
    .join('');

  const yTicks: number[] = [];
  const ySteps = 4;
  for (let i = 0; i <= ySteps; i++) yTicks.push(yMin + (i / ySteps) * yRange);
  const yTickLabels = yTicks
    .map(v => {
      const cy = py(v);
      const text = formatTickLabel(v);
      return `<line x1="${(PADDING.left - 4).toFixed(1)}" y1="${cy.toFixed(1)}" x2="${PADDING.left.toFixed(1)}" y2="${cy.toFixed(1)}" stroke="#94a3b8"/><text x="${(PADDING.left - 8).toFixed(1)}" y="${(cy + 3).toFixed(1)}" text-anchor="end" font-size="10" fill="#475569" font-family="ui-sans-serif, system-ui">${text}</text>`;
    })
    .join('');

  const xLabel = escapeXml(plot.xLabel ?? (useLogX ? 'log₁₀ dose' : 'x'));
  const yLabel = escapeXml(plot.yLabel ?? 'y');
  const title = escapeXml(plot.title ?? '');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${SVG_W}" height="${SVG_H}" viewBox="0 0 ${SVG_W} ${SVG_H}" font-family="ui-sans-serif, system-ui">
<rect x="0" y="0" width="${SVG_W}" height="${SVG_H}" fill="#ffffff"/>
${title ? `<text x="${SVG_W / 2}" y="16" text-anchor="middle" font-size="13" font-weight="600" fill="#0f172a">${title}</text>` : ''}
<rect x="${PADDING.left}" y="${PADDING.top}" width="${PLOT_W}" height="${PLOT_H}" fill="#f8fafc" stroke="#cbd5e1"/>
${xTickLabels}
${yTickLabels}
${pathD ? `<path d="${pathD}" fill="none" stroke="#dc2626" stroke-width="1.8"/>` : ''}
${points}
<text x="${SVG_W / 2}" y="${SVG_H - 8}" text-anchor="middle" font-size="11" fill="#334155">${xLabel}${useLogX ? ' (log scale)' : ''}</text>
<text x="14" y="${SVG_H / 2}" text-anchor="middle" font-size="11" fill="#334155" transform="rotate(-90 14 ${SVG_H / 2})">${yLabel}</text>
</svg>`;
}

function formatTickLabel(v: number): string {
  if (!Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  if (abs === 0) return '0';
  if (abs >= 1000 || abs < 0.001) return v.toExponential(1);
  return Number(v.toPrecision(3)).toString();
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function svgToDataUrl(svg: string): string {
  // btoa requires latin-1; use TextEncoder + base64 to handle UTF-8
  // (chart labels can include µ, ², etc.).
  const bytes = new TextEncoder().encode(svg);
  let binary = '';
  for (let i = 0; i < bytes.length; i++)
    binary += String.fromCharCode(bytes[i]);
  return `data:image/svg+xml;base64,${btoa(binary)}`;
}

/**
 * Map the backend `DatasetKind` enum to a user-facing label. Keep the
 * map terse — it's surfaced in a callout, not a paragraph.
 */
function formatDatasetKindLabel(kind: string): string {
  switch (kind) {
    case 'dose-response':
      return 'dose-response';
    case 'dose-response-multi':
      return 'multi-compound dose-response';
    case 'hts-plate':
      return 'HTS plate';
    case 'differential-expr':
      return 'differential-expression';
    case 'expression-matrix':
      return 'expression matrix';
    case 'survival':
      return 'survival';
    case 'generic-numeric':
      return 'numeric';
    case 'generic-categorical':
      return 'categorical';
    default:
      return kind;
  }
}

// ======== Deep Research markdown renderer (Phase A) ========

/**
 * Loose shapes for the deep-research result fields. Mirror the
 * backend `DeepResearchModeResult` interface but kept inline so the
 * doc-writer doesn't import a backend file.
 */
interface DeepResearchBundleSummaryForRender {
  documentCount?: number;
  themes?: string[];
  overallScope?: string;
  dataTypes?: Record<string, number>;
}
interface DeepResearchDocAnalysisForRender {
  documentId?: string;
  documentName?: string;
  summary?: string;
  keyFindings?: string[];
  themes?: string[];
  methodology?: string;
  dataType?: string;
}
interface DeepResearchPatternForRender {
  theme?: string;
  documents?: string[];
  evidence?: string[];
  strength?: 'strong' | 'moderate' | 'weak';
}
interface DeepResearchConflictForRender {
  type?: 'conflict' | 'gap';
  description?: string;
  documents?: string[];
  suggestedResolution?: string;
}
interface DeepResearchSynthesisForRender {
  synthesis?: string;
  commonPatterns?: string;
  conflictsGaps?: string;
  nextSteps?: string;
}

interface DeepResearchExtractionRowForRender {
  documentId?: string;
  documentName?: string;
  drug?: string;
  target?: string;
  assay?: string;
  dose?: string;
  ic50?: string;
  n?: number | string;
  pValue?: string;
  limitation?: string;
  extra?: Record<string, unknown>;
}
interface DeepResearchSourceQuoteForRender {
  text?: string;
  docName?: string;
  docId?: string;
  score?: number;
}

interface DeepResearchClaimForRender {
  text?: string;
  sources?: string[];
  support?: string;
  // Gap-3 — best-matching source chunk
  sourceQuote?: DeepResearchSourceQuoteForRender;
}
interface DeepResearchStrictConflictForRender {
  topic?: string;
  sideA?: DeepResearchClaimForRender;
  sideB?: DeepResearchClaimForRender;
  resolution?: string;
}
interface DeepResearchGapForRender {
  description?: string;
  suggestedPubMedQuery?: string;
  suggestedNextSteps?: string[];
  // Gap-3 — best-matching source chunk
  sourceQuote?: DeepResearchSourceQuoteForRender;
}
interface DeepResearchCapsuleForRender {
  bundleHash?: string;
  documents?: Array<{ name?: string; sha256?: string; sizeBytes?: number }>;
  themes?: string[];
  createdAt?: string;
  generator?: string;
}

interface DeepResearchRetractionFlagForRender {
  citationId?: string;
  type?: string;
  retracted?: boolean;
  flags?: string[];
  note?: string;
}

interface DeepResearchPayloadForRender {
  bundleSummary?: DeepResearchBundleSummaryForRender;
  documentAnalyses?: DeepResearchDocAnalysisForRender[];
  crossDocumentPatterns?: DeepResearchPatternForRender[];
  conflictsAndGaps?: DeepResearchConflictForRender[];
  synthesisStructure?: DeepResearchSynthesisForRender;
  // Phase B fields:
  extractionRows?: DeepResearchExtractionRowForRender[];
  conflicts?: DeepResearchStrictConflictForRender[];
  gaps?: DeepResearchGapForRender[];
  methodsParagraph?: string;
  methodsParagraphStyle?: string;
  capsule?: DeepResearchCapsuleForRender;
  // Gap-2 — retraction / predatory-journal flags
  retractionFlags?: DeepResearchRetractionFlagForRender[];
  // Gap-6 — soft warnings from non-critical pipeline failures
  processingNotes?: string[];
  // Gap-8 — newer PubMed papers on top themes not in this bundle
  suggestedAdditions?: Array<{
    theme?: string;
    title?: string;
    pmid?: string;
    year?: number;
    journal?: string;
  }>;
}

/**
 * Build a one-glance summary line of empty buckets so the user
 * doesn't see a wall of empty section headers when a small bundle
 * yields no conflicts / patterns / gaps. Returns an empty string
 * when nothing is empty (the synthesis was rich) — caller skips
 * the line entirely.
 */
function buildEmptyStatesLine(payload: DeepResearchPayloadForRender): string {
  const empties: string[] = [];
  if (
    !(payload.crossDocumentPatterns && payload.crossDocumentPatterns.length > 0)
  ) {
    empties.push('no cross-document patterns surfaced');
  }
  if (
    !(payload.conflicts && payload.conflicts.length > 0) &&
    !(payload.conflictsAndGaps && payload.conflictsAndGaps.length > 0)
  ) {
    empties.push('no conflicts found');
  }
  if (!(payload.gaps && payload.gaps.length > 0)) {
    empties.push('no explicit gaps identified');
  }
  if (empties.length === 0) return '';
  return `> 🟢 *Bundle is internally consistent: ${empties.join(', ')}.*`;
}

function renderDeepResearchMarkdown(
  payload: DeepResearchPayloadForRender
): string {
  const out: string[] = [];

  // Gap-2: surface retraction / predatory-journal flags FIRST.
  // PhDs need to know about a retracted source before they read
  // the synthesis built on top of it.
  if (payload.retractionFlags && payload.retractionFlags.length > 0) {
    const retracted = payload.retractionFlags.filter(f => f.retracted);
    out.push('### ⚠️ Citation integrity warnings');
    out.push('');
    if (retracted.length > 0) {
      out.push(
        `**${retracted.length} citation${retracted.length === 1 ? '' : 's'} flagged as retracted or under expression of concern.** Treat the synthesis claims that depend on them with extra caution.`
      );
      out.push('');
    }
    out.push('| Citation | Flags | Note |');
    out.push('|---|---|---|');
    for (const f of payload.retractionFlags) {
      const label = f.citationId ?? '—';
      const flags = (f.flags ?? []).map(x => `\`${x}\``).join(' ');
      const note = (f.note ?? '—').replace(/\|/g, '\\|');
      out.push(`| ${label} | ${flags} | ${note} |`);
    }
    out.push('');
  }

  // Bundle header — count + scope + theme chips.
  if (payload.bundleSummary) {
    const bs = payload.bundleSummary;
    out.push(`## Bundle overview`);
    out.push('');
    if (bs.documentCount !== undefined) {
      const dataTypeBreakdown = bs.dataTypes
        ? Object.entries(bs.dataTypes)
            .map(([k, v]) => `${v} ${k}`)
            .join(', ')
        : '';
      out.push(
        `${bs.documentCount} document${bs.documentCount === 1 ? '' : 's'}${dataTypeBreakdown ? ` (${dataTypeBreakdown})` : ''}.`
      );
      out.push('');
    }
    // Gap-6 — empty-state callout. When the bundle yields no
    // conflicts / patterns / gaps we want the user to see WHY the
    // synthesis is short, not a wall of empty section headers.
    const emptyLine = buildEmptyStatesLine(payload);
    if (emptyLine) {
      out.push(emptyLine);
      out.push('');
    }
    if (bs.overallScope) {
      out.push(`*${bs.overallScope}*`);
      out.push('');
    }
    if (bs.themes && bs.themes.length > 0) {
      out.push('**Themes:**');
      out.push('');
      for (const t of bs.themes) {
        // Plain text — themes are often full sentences from the
        // LLM. Backticks make BlockSuite render them as inline-code
        // (monospace + grey background), which looks awful for prose.
        out.push(`- ${t}`);
      }
      out.push('');
    }
  }

  // Per-document table. Reads as a Methods-section figure caption.
  if (payload.documentAnalyses && payload.documentAnalyses.length > 0) {
    out.push('### Per-document analysis');
    out.push('');
    out.push('| Document | Type | Summary | Key findings |');
    out.push('|---|---|---|---|');
    for (const d of payload.documentAnalyses) {
      const name = d.documentName ?? d.documentId ?? '—';
      const dataType = d.dataType ?? '—';
      const summary = (d.summary ?? '').replace(/\|/g, '\\|').slice(0, 160);
      const findings = d.keyFindings
        ? d.keyFindings
            .slice(0, 3)
            .map(f => f.replace(/\|/g, '\\|'))
            .join('; ')
        : '—';
      out.push(`| ${name} | ${dataType} | ${summary} | ${findings} |`);
    }
    out.push('');
  }

  // Phase B: structured extraction grid (one row per paper).
  if (payload.extractionRows && payload.extractionRows.length > 0) {
    const rows = payload.extractionRows;
    // Determine which optional columns to surface — only those any row populates.
    const optionalCols: Array<
      [keyof DeepResearchExtractionRowForRender, string]
    > = [
      ['drug', 'Drug'],
      ['target', 'Target'],
      ['assay', 'Assay'],
      ['dose', 'Dose'],
      ['ic50', 'IC50'],
      ['n', 'n'],
      ['pValue', 'p'],
      ['limitation', 'Limitation'],
    ];
    const usedCols = optionalCols.filter(([k]) =>
      rows.some(r => r[k] !== undefined && r[k] !== null && r[k] !== '')
    );
    if (usedCols.length > 0) {
      out.push('### Extraction grid');
      out.push('');
      const headers = ['Document', ...usedCols.map(([, label]) => label)];
      out.push(`| ${headers.join(' | ')} |`);
      out.push(`| ${headers.map(() => '---').join(' | ')} |`);
      for (const r of rows) {
        const name = r.documentName ?? r.documentId ?? '—';
        const cells = [
          name,
          ...usedCols.map(([k]) => {
            const v = r[k];
            if (v === undefined || v === null || v === '') return '—';
            return String(v).replace(/\|/g, '\\|');
          }),
        ];
        out.push(`| ${cells.join(' | ')} |`);
      }
      out.push('');
    }
  }

  // Synthesis sections — backend already structured them, just label.
  if (payload.synthesisStructure) {
    const s = payload.synthesisStructure;
    if (s.synthesis) {
      out.push('### Synthesis');
      out.push('');
      out.push(s.synthesis);
      out.push('');
    }
    if (s.commonPatterns) {
      out.push('### Common patterns');
      out.push('');
      out.push(s.commonPatterns);
      out.push('');
    }
    // Phase B: prefer the strict conflicts/gaps tables when present;
    // only render the loose `conflictsGaps` prose when neither exists.
    const hasStrictConflictsOrGaps =
      (payload.conflicts && payload.conflicts.length > 0) ||
      (payload.gaps && payload.gaps.length > 0);
    if (s.conflictsGaps && !hasStrictConflictsOrGaps) {
      out.push('### Conflicts & gaps');
      out.push('');
      out.push(s.conflictsGaps);
      out.push('');
    }
    if (s.nextSteps) {
      out.push('### Next steps');
      out.push('');
      out.push(s.nextSteps);
      out.push('');
    }
  }

  // Phase B: structured conflicts. Switched from a 4-col table to a
  // per-conflict grouped block (Gap-3) so each side's source-quote
  // can render as a blockquote *next to* the claim instead of being
  // squashed into a table cell.
  if (payload.conflicts && payload.conflicts.length > 0) {
    out.push('### Conflicts');
    out.push('');
    for (const c of payload.conflicts) {
      const topic = c.topic ?? 'Conflict';
      out.push(`#### ${topic}`);
      out.push('');
      const renderSide = (label: string, side: typeof c.sideA | undefined) => {
        if (!side) {
          out.push(`**${label}** — *(no claim)*`);
          out.push('');
          return;
        }
        const sources = (side.sources ?? []).map(s => `\`${s}\``).join(', ');
        const support = side.support ?? 'unspecified';
        out.push(
          `**${label}** — ${side.text ?? '—'} *(${support}; sources: ${sources || '—'})*`
        );
        if (side.sourceQuote?.text) {
          const sq = side.sourceQuote;
          const lowConfidence =
            (sq.score ?? Number.POSITIVE_INFINITY) < 1
              ? ' *(low confidence)*'
              : '';
          out.push('');
          out.push(`> 📖 *${sq.docName ?? '—'}*${lowConfidence}`);
          // Indent the chunk lines with `> ` so BlockSuite groups
          // them as a single quote block.
          for (const line of (sq.text ?? '').split(/\n+/)) {
            out.push(`> ${line.trim()}`);
          }
        }
        out.push('');
      };
      renderSide('Side A', c.sideA);
      renderSide('Side B', c.sideB);
      if (c.resolution) {
        out.push(`**Resolution:** ${c.resolution}`);
        out.push('');
      }
    }
  }

  // Phase B: structured gaps with draft PubMed queries.
  if (payload.gaps && payload.gaps.length > 0) {
    out.push('### Gaps');
    out.push('');
    for (const g of payload.gaps) {
      out.push(`- 🔍 ${g.description ?? '(no description)'}`);
      if (g.suggestedPubMedQuery) {
        out.push(`    - Draft PubMed query: \`${g.suggestedPubMedQuery}\``);
      }
      if (g.suggestedNextSteps && g.suggestedNextSteps.length > 0) {
        for (const step of g.suggestedNextSteps) {
          out.push(`    - Next step: ${step}`);
        }
      }
      // Gap-3 — source quote attached by attachSourceQuotes()
      if (g.sourceQuote?.text) {
        const sq = g.sourceQuote;
        const lowConfidence =
          (sq.score ?? Number.POSITIVE_INFINITY) < 1
            ? ' *(low confidence)*'
            : '';
        const fullText = sq.text ?? '';
        out.push(
          `    - 📖 *${sq.docName ?? '—'}* — "${fullText.slice(0, 240)}${fullText.length > 240 ? '…' : ''}"${lowConfidence}`
        );
      }
    }
    out.push('');
  }

  // Phase B: paper-ready Methods paragraph.
  if (payload.methodsParagraph && payload.methodsParagraph.trim().length > 0) {
    const styleLabel = payload.methodsParagraphStyle
      ? ` (${payload.methodsParagraphStyle.charAt(0).toUpperCase()}${payload.methodsParagraphStyle.slice(1)} style)`
      : '';
    out.push(`### Methods paragraph${styleLabel}`);
    out.push('');
    out.push('*Paste this into your manuscript\u2019s Methods section.*');
    out.push('');
    const lines = payload.methodsParagraph.split(/\n+/);
    for (const line of lines) {
      out.push(`> ${line.trim()}`);
    }
    out.push('');
  }

  // Gap-8 — Living bundle: newer PubMed papers on top themes that
  // weren't in the original bundle. One-click "Re-run with these
  // added" closes the loop into a living review.
  if (payload.suggestedAdditions && payload.suggestedAdditions.length > 0) {
    out.push('### Suggested additions');
    out.push('');
    out.push(
      '*Recent PubMed papers on this bundle\u2019s themes that aren\u2019t in the bundle yet. Re-run with one or more added to see if they shift the synthesis.*'
    );
    out.push('');
    out.push('| Theme | Title | Year | Journal | Link |');
    out.push('|---|---|---|---|---|');
    for (const s of payload.suggestedAdditions) {
      const theme = s.theme ?? '—';
      const title = (s.title ?? '—').replace(/\|/g, '\\|');
      const year = s.year ?? '—';
      const journal = (s.journal ?? '—').replace(/\|/g, '\\|');
      const link = s.pmid
        ? `[PMID:${s.pmid}](https://pubmed.ncbi.nlm.nih.gov/${s.pmid}/)`
        : '—';
      out.push(`| ${theme} | ${title} | ${year} | ${journal} | ${link} |`);
    }
    out.push('');
  }

  // Gap-6 — processing notes (soft warnings).
  if (payload.processingNotes && payload.processingNotes.length > 0) {
    out.push('### Processing notes');
    out.push('');
    out.push(
      '*The synthesis above is complete; these are non-critical warnings worth knowing about.*'
    );
    out.push('');
    for (const n of payload.processingNotes) {
      out.push(`- ⚠️ ${n}`);
    }
    out.push('');
  }

  // Phase B: bundle capsule (manifest of doc names + hashes).
  if (
    payload.capsule &&
    payload.capsule.documents &&
    payload.capsule.documents.length > 0
  ) {
    const cap = payload.capsule;
    out.push('### Reproducibility capsule');
    out.push('');
    out.push(`- **Bundle hash:** \`${cap.bundleHash ?? '—'}\``);
    if (cap.themes && cap.themes.length > 0) {
      // Themes can be full sentences — plain comma list reads cleaner
      // than backticked chunks (which render as monospace boxes).
      out.push(`- **Themes:** ${cap.themes.join('; ')}`);
    }
    if (cap.createdAt) {
      out.push(
        `- **Generated:** ${cap.createdAt} (${cap.generator ?? 'curiescious'})`
      );
    }
    out.push('');
    out.push('| Document | SHA-256 | Bytes |');
    out.push('|---|---|---|');
    for (const d of cap.documents ?? []) {
      out.push(
        `| ${d.name ?? '—'} | \`${(d.sha256 ?? '').slice(0, 12)}…\` | ${d.sizeBytes ?? '—'} |`
      );
    }
    out.push('');
  }

  // Pattern list as fallback when synthesis wasn't structured.
  if (
    !payload.synthesisStructure?.commonPatterns &&
    payload.crossDocumentPatterns &&
    payload.crossDocumentPatterns.length > 0
  ) {
    out.push('### Cross-document patterns');
    out.push('');
    for (const p of payload.crossDocumentPatterns) {
      const strengthBadge = p.strength ? ` *(${p.strength})*` : '';
      out.push(`- **${p.theme ?? 'Pattern'}**${strengthBadge}`);
      if (p.documents && p.documents.length > 0) {
        out.push(
          `    - Sources: ${p.documents.map(d => `\`${d}\``).join(', ')}`
        );
      }
      if (p.evidence && p.evidence.length > 0) {
        out.push(`    - Evidence: ${p.evidence.slice(0, 2).join(' / ')}`);
      }
    }
    out.push('');
  }

  // Conflicts list as fallback when synthesis wasn't structured.
  if (
    !payload.synthesisStructure?.conflictsGaps &&
    payload.conflictsAndGaps &&
    payload.conflictsAndGaps.length > 0
  ) {
    out.push('### Conflicts & gaps');
    out.push('');
    for (const c of payload.conflictsAndGaps) {
      const typeLabel = c.type === 'gap' ? '🔍 Gap' : '⚠️ Conflict';
      out.push(`- ${typeLabel} — ${c.description ?? '(no description)'}`);
      if (c.documents && c.documents.length > 0) {
        out.push(
          `    - Sources: ${c.documents.map(d => `\`${d}\``).join(', ')}`
        );
      }
      if (c.suggestedResolution) {
        out.push(`    - Suggested resolution: ${c.suggestedResolution}`);
      }
    }
    out.push('');
  }

  return out.join('\n');
}
